import React from "react";
import { Link } from "react-router-dom";
import DelNovaLOGO from "../../images/DelNova_Logo_New.png";

export default function FormulDevelopServicesRedesign() {
  const [activeHub, setActiveHub] = React.useState("core");
  const [activeSegment, setActiveSegment] = React.useState(0);

  const hubs = {
    core: {
      title: "Core Capabilities",
      subtitle: "Dosage Forms & LCM",
      nodes: [
        { name: "Solid Oral", desc: "Granulation, powder compression, and tablet coating for standard tablet & capsule forms.", gradient: "from-amber-500 to-orange-500", color: "text-amber-600", bg: "bg-amber-50" },
        { name: "Sterile Injectables", desc: "Liquid and lyophilized sterile solutions engineered for rapid, controlled systemic action.", gradient: "from-rose-500 to-red-500", color: "text-rose-600", bg: "bg-rose-50" },
        { name: "Platform Tech", desc: "Advanced patented delivery systems like EzeGest™, DISSOLVR™, and CapMelt™.", gradient: "from-teal-500 to-emerald-500", color: "text-teal-600", bg: "bg-teal-50" },
        { name: "Lifecycle LCM", desc: "LCM strategies including generic line extensions, new strengths, and OTC adaptations.", gradient: "from-sky-500 to-blue-500", color: "text-sky-600", bg: "bg-sky-50" },
        { name: "Liquids & Semisolids", desc: "Stable solutions, suspensions, emulsions, gels, and ointments for oral & topical use.", gradient: "from-blue-500 to-indigo-500", color: "text-blue-600", bg: "bg-blue-50" }
      ]
    },
    delivery: {
      title: "Advanced Delivery",
      subtitle: "Targeted Release Formulations",
      nodes: [
        { name: "ODT Formulations", desc: "Oral Disintegrating Tablets that dissolve rapidly in saliva without requiring water.", gradient: "from-amber-500 to-orange-500", color: "text-amber-600", bg: "bg-amber-50" },
        { name: "Modified Release", desc: "Controlled, sustained, and pulsatile drug delivery forms tailored to therapeutic profiles.", gradient: "from-rose-500 to-red-500", color: "text-rose-600", bg: "bg-rose-50" },
        { name: "Delayed Release", desc: "Enteric coatings designed to resist gastric acid and release active ingredients in the intestine.", gradient: "from-teal-500 to-emerald-500", color: "text-teal-600", bg: "bg-teal-50" },
        { name: "MUPS Technology", desc: "Multi-Unit Particulate Systems utilizing compressed pellets for uniform GI transit.", gradient: "from-sky-500 to-blue-500", color: "text-sky-600", bg: "bg-sky-50" },
        { name: "Injectable Focus", desc: "Sterile suspensions and lipid-based formulations for extended release profiles.", gradient: "from-blue-500 to-indigo-500", color: "text-blue-600", bg: "bg-blue-50" }
      ]
    },
    processing: {
      title: "Novel Processing",
      subtitle: "Bioavailability Enhancement",
      nodes: [
        { name: "ODF Technology", desc: "Oral Dissolving Films providing fast oral dissolution, easy dosing, and portability.", gradient: "from-amber-500 to-orange-500", color: "text-amber-600", bg: "bg-amber-50" },
        { name: "Bioavailability", desc: "Excipient profiling, lipid-based systems, and solid dispersions to enhance solubility.", gradient: "from-rose-500 to-red-500", color: "text-rose-600", bg: "bg-rose-50" },
        { name: "Topical Formulations", desc: "Targeted localized delivery creams, ointments, transdermal patches, and sprays.", gradient: "from-teal-500 to-emerald-500", color: "text-teal-600", bg: "bg-teal-50" },
        { name: "Twin Screw", desc: "High-shear wet and dry twin screw granulation lines for continuous solid manufacturing.", gradient: "from-sky-500 to-blue-500", color: "text-sky-600", bg: "bg-sky-50" },
        { name: "HME Processing", desc: "Hot Melt Extrusion lines creating amorphous solid dispersions for insoluble compounds.", gradient: "from-blue-500 to-indigo-500", color: "text-blue-600", bg: "bg-blue-50" }
      ]
    }
  };

  const categories = [
    {
      title: "Clinical & Early-Phase Development",
      description: "Fast-track formulations engineered to transition drug candidates safely and efficiently from preclinical stages to clinical trials.",
      icon: (
        // Beaker Icon
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      items: [
        "Phase I - IV comprehensive formulation development",
        "First In Human (FIH) dose formulation engineering",
        "Clinical trials material (CTM) manufacturing support",
        "Micro-dosing compatibility and capsule filling",
        "PKPD-backed dose escalation validation"
      ]
    },
    {
      title: "Commercial & Lifecycle Development",
      description: "Strategic scaling, generic optimization, and product line extensions to maximize commercial exclusivity and patient reach.",
      icon: (
        // Trending up / Lifecycle Icon
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      items: [
        "Complex Generics and Differentiated Formulations",
        "Over-the-counter (OTC) product development",
        "Life Cycle Management (LCM) line extensions",
        "Patent non-infringing formulation pathways",
        "Pediatric dosage adaptations and taste masking",
        "Robust process scalability and technology transfer"
      ]
    }
  ];

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800">
      {/* Hero Header */}
      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-blue-deep">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 mix-blend-overlay"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1579165466541-71702b859ec1?q=80&w=1920&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Innovative Scaling Phase
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            Formulation Development Services
          </h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-slate-300 font-light leading-relaxed">
            Patient-focused innovation from early phase development to commercial lifecycle management.<br /> We translate molecules into stable, scalable, and bioequivalent drug products.
          </p>
        </div>
      </section>

      {/* Main Body Grid */}
      <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Image Stack & Overview */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-white border border-slate-100 rounded-2xl p-5 md:p-6 shadow-sm relative overflow-hidden space-y-6 flex flex-col items-center">
              {/* Hub Selection Segmented Control */}
              <div className="w-full bg-slate-50 p-1 rounded-xl flex border border-slate-100/80">
                {Object.keys(hubs).map((hubKey) => (
                  <button
                    key={hubKey}
                    onClick={() => {
                      setActiveHub(hubKey);
                      setActiveSegment(0);
                    }}
                    className={`flex-1 text-[10px] md:text-xs font-bold py-2 rounded-lg transition-all ${
                      activeHub === hubKey
                        ? "bg-white text-brand-blue shadow-sm border border-slate-100"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {hubs[hubKey].title}
                  </button>
                ))}
              </div>

              {/* Hub Info Banner */}
              <div className="text-center">
                <span className="text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-0.5 rounded-full">
                  {hubs[activeHub].subtitle}
                </span>
              </div>

              {/* Orbiting pentagon wheel for Desktop / Tablet */}
              <div className="hidden md:block w-full max-w-[280px] aspect-square relative mx-auto my-4">
                {/* SVG Connecting Lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" viewBox="0 0 100 100">
                  <line x1="50" y1="50" x2="50" y2="14" stroke="#E2E8F0" strokeWidth="0.8" />
                  <line x1="50" y1="50" x2="84" y2="39" stroke="#E2E8F0" strokeWidth="0.8" />
                  <line x1="50" y1="50" x2="71" y2="79" stroke="#E2E8F0" strokeWidth="0.8" />
                  <line x1="50" y1="50" x2="29" y2="79" stroke="#E2E8F0" strokeWidth="0.8" />
                  <line x1="50" y1="50" x2="16" y2="39" stroke="#E2E8F0" strokeWidth="0.8" />
                  {/* Glowing Orbit Rings */}
                  <circle cx="50" cy="50" r="36" fill="none" stroke="#F1F5F9" strokeWidth="2" strokeDasharray="3 3" />
                </svg>

                {/* Central Logo Hub */}
                <div className="w-14 h-14 rounded-full bg-white border border-slate-100 flex items-center justify-center shadow-lg absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 ring-4 ring-brand-blue/5">
                  <img src={DelNovaLOGO} alt="DelNova" className="h-8 w-auto object-contain" />
                </div>

                {/* Nodes orbiting around the hub */}
                {hubs[activeHub].nodes.map((node, index) => {
                  const positions = [
                    { left: "50%", top: "14%" },
                    { left: "84%", top: "39%" },
                    { left: "71%", top: "79%" },
                    { left: "29%", top: "79%" },
                    { left: "16%", top: "39%" }
                  ];
                  const isActive = activeSegment === index;
                  return (
                    <button
                      key={index}
                      onMouseEnter={() => setActiveSegment(index)}
                      className={`w-11 h-11 rounded-full absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center text-[10px] font-bold text-center text-white shadow-md transition-all duration-300 z-10 bg-gradient-to-br ${
                        node.gradient
                      } ${
                        isActive
                          ? "scale-110 ring-4 ring-white shadow-xl translate-y-[-2px]"
                          : "opacity-80 hover:opacity-100 hover:scale-105"
                      }`}
                      style={positions[index]}
                      title={node.name}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>

              {/* Mobile layout selector tabs */}
              <div className="md:hidden w-full flex flex-wrap gap-2 justify-center">
                {hubs[activeHub].nodes.map((node, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveSegment(index)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      activeSegment === index
                        ? `bg-gradient-to-br ${node.gradient} text-white border-transparent shadow-sm`
                        : "bg-slate-50 text-slate-600 border-slate-200"
                    }`}
                  >
                    {node.name}
                  </button>
                ))}
              </div>

              {/* Interactive Detail Box */}
              <div className={`w-full p-4 rounded-xl border transition-all duration-300 text-left ${hubs[activeHub].nodes[activeSegment].bg} border-slate-100`}>
                <div className="flex items-center space-x-2 mb-2">
                  <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold shadow-sm">
                    {activeSegment + 1}
                  </span>
                  <h4 className={`text-sm font-bold ${hubs[activeHub].nodes[activeSegment].color}`}>
                    {hubs[activeHub].nodes[activeSegment].name}
                  </h4>
                </div>
                <p className="text-slate-600 text-xs font-light leading-relaxed">
                  {hubs[activeHub].nodes[activeSegment].desc}
                </p>
              </div>

              <div className="border-t border-slate-100 pt-4 w-full">
                <h3 className="text-sm font-bold text-brand-blue mb-1">Scale-Up &amp; Scalability</h3>
                <p className="text-slate-500 text-xs font-light leading-relaxed">
                  Our formulation team balances advanced drug delivery technologies with scalable engineering. By incorporating PKPD insights and robust excipient profiling, we ensure that every formulation developed at our centre meets clinical bioequivalence and is ready for commercial technology transfer.
                </p>
              </div>
            </div>

            {/* CTA Widget */}
            <div className="bg-gradient-to-br from-brand-blue to-brand-blue-light text-white rounded-2xl p-8 shadow-md flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-brand-teal/10 rounded-full blur-xl group-hover:scale-110 transition-transform duration-500" />
              <div>
                <h4 className="text-lg font-bold mb-2">Ready to Develop Your Product?</h4>
                <p className="text-slate-300 text-sm font-light mb-6 leading-relaxed">
                  Partner with our formulation experts to design stable dosage forms, out-license specification templates, or establish non-infringing pathways.
                </p>
              </div>
              <Link
                to="/ContactUs"
                state={{ from: "Inquiry: Formulation Development Services" }}
                className="w-fit inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-full shadow-sm text-brand-blue bg-white hover:bg-brand-teal hover:text-white transition-all duration-200"
              >
                Inquire About Formulation Development
              </Link>
            </div>
          </div>

          {/* Right Column: Categorized Cards */}
          <div className="lg:col-span-7 space-y-8">
            {categories.map((cat, idx) => (
              <div
                key={idx}
                className="bg-white border border-slate-100 hover:border-brand-teal/20 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 group"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal group-hover:bg-brand-teal group-hover:text-white transition-colors duration-300 shrink-0">
                    {cat.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-brand-blue mb-2">{cat.title}</h3>
                    <p className="text-slate-500 text-sm font-light leading-relaxed mb-6">{cat.description}</p>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-6">
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {cat.items.map((item, itemIdx) => (
                      <li key={itemIdx} className="flex items-start text-slate-600 text-xs md:text-sm font-light">
                        <svg className="w-4 h-4 text-brand-teal mr-2 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
